
import {mapActions, mapState} from "vuex";
import Timestamp from '../../components/shared/Timestamp.vue';
import io from "socket.io-client";

const socket = io('https://draxjb.com/', {path: '/wss'});
import {defineComponent} from "vue";
import store, {ActionTypes} from "../../store";
import {Notyf} from 'notyf';

const notyf = new Notyf({
  duration: 1000,
  position: {
    x: 'center',
    y: 'center',
  },
  types: [
    {
      type: 'information',
      background: 'green',
      duration: 2000,
      dismissible: true
    },
    {
      type: 'error',
      background: 'indianred',
      duration: 2000,
      dismissible: true
    }
  ]
});

type EntryType = {
  Strategy: string;
  Mid: any;
  Change: number;
  '3PM Close': number;
  '5PM Close': number;
  isFrozen?: boolean;
  isChecked?: boolean;
  aboveThreshold?: number; // Added for user input
  belowThreshold?: number; // Added for user input
};

export default defineComponent({
  components: {
    Timestamp,
  },
  data() {
    return {
      gridData: [] as EntryType[],
      timestampData: 'Loading...', // Placeholder text before real data is loaded
      entry: {} as EntryType, // Currently selected or new entry for operations
      initialLoadDone: false, // Flag to track if initial load is done
    };
  },
  created() {
    // Connect to WebSocket and set up listeners
    socket.emit('get_alerts', this.user.id); // Request initial data for alerts
    socket.on('strategies_alertsgrid', async (data) => { // Made this async to use await inside
      if (data.user_id == this.user.id) {
        // Check if the data is intended for the current user
        const newData = JSON.parse(data.strategies_alertsgrid); // Parse incoming data
        this.updateGridData(newData); // Update grid data while preserving inputs
        this.timestampData = data.lastupdated; // Update last updated timestamp

        // Call loadValues() to fetch and merge additional data from the API
        // Only call loadValues() if it hasn't been called yet
        if (!this.initialLoadDone) {
          await this.loadValues();
          this.initialLoadDone = true; // Prevent future calls
        }
      }
    });
  },
  computed: {
    ...mapState(['user']) // Mapping Vuex state to local computed properties
  },
  mounted() {
    // Reconnect the socket when the component is fully mounted
    socket.disconnect();
    socket.connect();
    // Periodic request for new data
    window.setInterval(() => {
      socket.emit('get_alerts', this.user.id);
    }, 70); // Interval set for 70ms could be adjusted based on actual requirements
  },
  beforeUnmount() {
    // Cleanup before the component is destroyed
    socket.off(`strategies_alertsgrid`); // Remove listener
    socket.disconnect(); // Disconnect from WebSocket
  },
  methods: {
    ...mapActions({
      saveValuesToAPI: ActionTypes.SEND_TICK_DATA, // Mapping Vuex actions
      fetchValuesFromAPI: ActionTypes.LOAD_TICK_DATA, // Mapping Vuex actions
    }),
    clearValues(entry: any) {
      // Reset the threshold values for the entry
      entry.aboveThreshold = undefined;
      entry.belowThreshold = undefined;
      notyf.open({
        type: 'information',
        message: 'All values were cleared successfully! Please Save to confirm.',
      });
    },
    getRowClass(entry: EntryType) {
      // Convert Mid from string to float
      const midValue = parseFloat(entry.Mid);
      // Since aboveThreshold and belowThreshold are already floats, directly compare
      if (entry.aboveThreshold !== undefined && midValue > entry.aboveThreshold) {
        return 'row-dark-green'; // Class when Mid is greater than Notification (>)
      } else if (entry.belowThreshold !== undefined && midValue < entry.belowThreshold) {
        return 'row-dark-red'; // Class when Mid is less than Notification (<)
      }
      return ''; // Default class (no specific color)
    },
    async loadValues() {
      try {
        // Assuming this.fetchValuesFromAPI() correctly fetches the data and the response format is as shown above
        const response = await this.fetchValuesFromAPI(this.user); // Make sure this fetches the data as expected
        const loadedValues = response; // Assuming response is already the data array you showed

        // Update gridData with loadedValues
        this.gridData = this.gridData.map(entry => {
          // Find the matching loaded entry by strategy
          const loadedEntry = loadedValues.find((lv: any) => lv.strategy === entry.Strategy);
          if (loadedEntry) {
            // If a matching loaded entry is found, update the current entry's thresholds
            return {
              ...entry,
              aboveThreshold: loadedEntry.aboveThreshold,
              belowThreshold: loadedEntry.belowThreshold,
            };
          }
          // If no matching loaded entry is found, return the entry as is
          return entry;
        });
      } catch (error) {
      }
    },
    async saveValues(entry: EntryType) {
      // Save current values for a specific entry
      try {
        const response = await this.saveValuesToAPI({
          strategy: entry.Strategy,
          aboveThreshold: entry.aboveThreshold,
          belowThreshold: entry.belowThreshold,
          user: this.user.id,
        });
        if (response.status === 200) {
          notyf.open({
            type: 'information',
            message: 'Values saved successfully!',
          });
        } else {
          notyf.open({
            type: 'information',
            message: 'Values saved successfully!',
          })
        }
      } catch (error) {
        notyf.open({
          type: 'error',
          message: 'Error saving values!',
        });
      }
    },
    updateGridData(newData: any) {
      // Update grid data with new data while preserving user input
      const updatedData = newData.map((newEntry: any) => {
        const existingEntry = this.gridData.find(entry => entry.Strategy === newEntry.Strategy);
        if (existingEntry) {
          return {
            ...newEntry,
            aboveThreshold: existingEntry.aboveThreshold,
            belowThreshold: existingEntry.belowThreshold,
          };
        }
        return newEntry;
      });
      this.gridData = updatedData;
    },
    filteredGridData(gridData: any) {
      // Filter grid data based on some criteria (implementation not shown)
      return gridData.filter((entry: any) => true); // Placeholder filter
    },
    hasAllValuesExceptChart(entry: any) {
      // Check if an entry has all necessary values except chart (implementation not shown)
      return true; // Placeholder return value
    },
  },
});
